/**
 * Дт-Кт журнал документа — аналог кнопки Дт-Кт у 1С (2026-08-09).
 *
 * Показує РЕЗУЛЬТАТ проведення: бух-проводки (acc.entries) + рухи
 * регістрів (склад/партії/розрахунки/ПДВ/...). Універсальний для всіх
 * проводящих документів: DocumentToolbar рендерить кнопку «дт-кт», коли
 * editor передав entriesDoc={{type, id}} і документ проведено.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';

export interface DtKtDocRef {
  /** document_type у acc.entries, напр. 'doc.customs_declarations'. */
  type: string;
  /** Порожній на new-документі → кнопка «дт-кт» неактивна до збереження. */
  id?: string;
}

interface JournalEntry {
  line_number: number | null;
  entry_date: string | null;
  dt_code: string;
  cr_code: string;
  dt_subconto: string[];
  cr_subconto: string[];
  amount: string;
  tax_amount: string | null;
  dt_quantity: string | null;
  cr_quantity: string | null;
  dt_currency_amount: string | null;
  cr_currency_amount: string | null;
  dt_currency: string | null;
  cr_currency: string | null;
  content: string | null;
}

interface JournalResponse {
  entries: JournalEntry[];
  entries_total: string;
  registers: Array<{ table: string; title: string; rows: Record<string, string>[] }>;
}

/** Людські назви колонок регістрів (unknown → як є). */
const REG_COL_LABELS: Record<string, string> = {
  movement_date: 'Дата',
  direction: 'Напрям',
  quantity: 'Кількість',
  amount: 'Сума',
  amount_without_vat: 'Сума без ПДВ',
  vat_amount: 'ПДВ',
  warehouse_id: 'Склад',
  nomenclature_id: 'Номенклатура',
  characteristic_id: 'Характеристика',
  series_id: 'Серія',
  quality_id: 'Якість',
  contractor_id: 'Контрагент',
  contract_id: 'Договір',
  employee_id: 'Працівник',
  currency_id: 'Валюта',
  cash_flow_item_id: 'Стаття руху коштів',
  cash_register_id: 'Каса',
  bank_account_id: 'Банк. рахунок',
  batch_key_id: 'Партія',
  settlement_type: 'Вид розрахунків',
  line_number: '№ рядка',
  account_code: 'Рахунок',
  cost: 'Собівартість',
  cost_amount: 'Собівартість',
  vat_base: 'База ПДВ',
  vat_rate_id: 'Ставка ПДВ',
  status: 'Статус',
  settlement_doc_type: 'Документ розрахунків',
  reserve_document_type: 'Документ резерву',
  entry_date: 'Дата',
  operation_type: 'Вид операції',
  amount_before_discount: 'Сума до знижки',
  cash_kind: 'Вид коштів',
  customer_order_id: 'Замовлення покупця',
  supplier_order_id: 'Замовлення постачальнику',
  department_id: 'Підрозділ',
  nomenclature_group_id: 'Номенклатурна група',
  days: 'Днів',
  declaration_line: 'Рядок декларації',
  fund_marker: 'Фонд',
  tax_type: 'Податок',
  vacation_type: 'Вид відпустки',
  op_code: 'Код операції',
  operation_code: 'Код операції',
  settlement_amount: 'Сума розрахунку',
  vat_paid: 'ПДВ сплачено',
  vat_recognized: 'ПДВ визнано',
  vat_remaining: 'ПДВ залишок',
  pn_registered: 'ПН зареєстрована',
  is_settled: 'Закрито',
  is_below_normal_price: 'Нижче звичайної ціни',
};

/** Статуси ПДВ-регістрів → людською. */
const STATUS_LABELS: Record<string, string> = {
  PENDING: 'очікує ПН',
  CONFIRMED: 'підтверджено',
  APPLIED: 'застосовано',
  RECEIVED: 'отримано',
  CANCELLED: 'скасовано',
};

/** Значення клітинки регістру → людський вигляд. */
function regCell(col: string, v: string | undefined): string {
  if (v == null) return '';
  if (col === 'direction') return v === '+' ? '+ прихід' : v === '-' ? '− розхід' : v;
  if (col === 'status') return STATUS_LABELS[v] ?? v;
  if (/^(doc|tax|core)\.[a-z_]+$/.test(v)) return DOC_TABLE_NAMES_UK[v] ?? v;
  if (v === 'true') return 'так';
  if (v === 'false') return 'ні';
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleDateString('uk-UA');
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(v + 'T00:00:00').toLocaleDateString('uk-UA');
  if (/^-?\d+\.\d+$/.test(v)) return fmtNum(v);
  return v;
}

const fmtNum = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : v;
};

export function DtKtModal({ doc, onClose }: { doc: DtKtDocRef & { id: string }; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['dtkt-journal', doc.type, doc.id],
    queryFn: () =>
      api.get<JournalResponse>('/documents/entries-journal', {
        params: { document_type: doc.type, document_id: doc.id },
      }).then((r) => r.data),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div className="card" style={{ width: 'min(1100px, 94vw)', maxHeight: '84vh',
        overflowY: 'auto', margin: 0 }}
        onClick={(e) => e.stopPropagation()}>
        <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Дт-Кт: проводки та рухи документа</h3>
          <button style={{ marginLeft: 'auto' }} onClick={onClose}>закрити</button>
        </div>

        {q.isLoading && <p style={{ color: 'var(--muted)' }}>Завантаження…</p>}
        {q.isError && <div className="error">Не вдалося завантажити проводки</div>}

        {q.data && (
          <>
            <h4 style={{ margin: '0.25rem 0 0.4rem' }}>Бухгалтерські проводки</h4>
            {q.data.entries.length === 0 ? (
              // Рішення власника 2026-08-09: до проведення рахунки МОЖНА
              // змінювати (вкладка «Рахунки обліку»); після — лише перегляд,
              // зміна тільки через розпроведення.
              <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
                Проводок немає. Причини: документ ще не проведено (рахунки
                можна змінити зараз — вкладка «Рахунки обліку»); або документ
                за своєю природою проводок не створює (переміщення склад-склад,
                інвентаризація без розбіжностей) — тоді дивіться рухи
                регістрів нижче, якщо вони є.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
                      <th style={{ padding: '0.45rem', width: 30 }}>#</th>
                      <th style={{ padding: '0.45rem', width: 70 }}>Дт</th>
                      <th style={{ padding: '0.45rem' }}>Субконто Дт</th>
                      <th style={{ padding: '0.45rem', width: 70 }}>Кт</th>
                      <th style={{ padding: '0.45rem' }}>Субконто Кт</th>
                      <th style={{ padding: '0.45rem', width: 120, textAlign: 'right' }}>Сума</th>
                      <th style={{ padding: '0.45rem' }}>Зміст</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.entries.map((e, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
                        <td style={{ padding: '0.45rem', color: 'var(--muted)' }}>{i + 1}</td>
                        <td style={{ padding: '0.45rem', fontWeight: 700 }}>{e.dt_code}</td>
                        <td style={{ padding: '0.45rem' }}>
                          {e.dt_subconto.map((s2, j) => <div key={j}>{s2}</div>)}
                          {e.dt_quantity && (
                            <div style={{ color: 'var(--muted)' }}>к-сть: {e.dt_quantity}</div>
                          )}
                          {e.dt_currency_amount && (
                            <div style={{ color: 'var(--muted)' }}>
                              {fmtNum(e.dt_currency_amount)} {e.dt_currency ?? ''}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '0.45rem', fontWeight: 700 }}>{e.cr_code}</td>
                        <td style={{ padding: '0.45rem' }}>
                          {e.cr_subconto.map((s2, j) => <div key={j}>{s2}</div>)}
                          {e.cr_quantity && (
                            <div style={{ color: 'var(--muted)' }}>к-сть: {e.cr_quantity}</div>
                          )}
                          {e.cr_currency_amount && (
                            <div style={{ color: 'var(--muted)' }}>
                              {fmtNum(e.cr_currency_amount)} {e.cr_currency ?? ''}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '0.45rem', textAlign: 'right', fontWeight: 600 }}>
                          {fmtNum(e.amount)}
                        </td>
                        <td style={{ padding: '0.45rem', color: 'var(--text-secondary)' }}>{e.content}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border-strong)', fontWeight: 700 }}>
                      <td colSpan={5} style={{ padding: '0.45rem', textAlign: 'right' }}>Разом:</td>
                      <td style={{ padding: '0.45rem', textAlign: 'right' }}>
                        {fmtNum(q.data.entries_total)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {q.data.registers.map((reg) => {
              const cols = [...new Set(reg.rows.flatMap((r) => Object.keys(r)))];
              return (
                <div key={reg.table} style={{ marginTop: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.4rem' }}>{reg.title}</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
                          {cols.map((c) => (
                            <th key={c} style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}>
                              {REG_COL_LABELS[c] ?? c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reg.rows.map((r, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                            {cols.map((c) => (
                              <td key={c} style={{ padding: '0.4rem' }}>
                                {regCell(c, r[c])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
            {q.data.entries.length > 0 && q.data.registers.length === 0 && (
              <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                Рухів у регістрах цей документ не створює.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Готова кнопка «дт-кт» + модал (рішення власника 2026-08-09: у editor'ах
 * з вкладками «Рахунки обліку»/«Податковий облік» кнопка стоїть ПОРУЧ з
 * ними, а не в тулбарі — результат поряд з налаштуваннями).
 */
export function DtKtButton({ doc }: { doc: DtKtDocRef }) {
  const [open, setOpen] = useState(false);
  // Live-фікс 2026-08-09: на new-документі route-параметр id = 'new'
  // (НЕ undefined) → кнопка вважала док збереженим і слала
  // document_id=new (422). Валідний лише UUID-подібний id.
  const docId = doc.id && doc.id !== 'new' ? doc.id : undefined;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={!docId}
        title={docId
          ? 'Проводки та рухи регістрів документа (як Дт-Кт у 1С)'
          : 'Спершу збережіть документ'}>
        дт-кт
      </button>
      {open && docId && (
        <DtKtModal doc={{ type: doc.type, id: docId }} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
